import type { MessageId, Translator } from '@xdocs/i18n';

/** Humanizes a `categories.slug` fallback when its `nameKey` isn't resolvable
 * through the shared catalog (see `resolveCategoryLabel` below). */
function humanizeSlug(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Resolves a category's display label (F31). `categorySchema.nameKey` is
 * documented as "a catalog key (localized), never a stored English string"
 * (CODE_GUIDELINES §20 i18n standard) — but `categorize-worker`-created
 * categories are dynamic at RUNTIME ("new categories emerge from content",
 * F31), so their `nameKey` can't be one of this build's compile-time-known
 * catalog ids the way every other `t()` call site is. `packages/i18n/**` is
 * outside this task's ownership boundary, so this can't seed a real catalog
 * entry per category either. Falls back to a humanized `slug` for any key
 * the catalog can't resolve, rather than crashing (the shared `t()` throws
 * in dev on an unknown message id) — see this task's returned issues list
 * for the proper fix (a runtime-resolvable category name column/catalog).
 */
export function resolveCategoryLabel(t: Translator['t'], category: { slug: string; nameKey: string }): string {
  try {
    return t(category.nameKey as MessageId);
  } catch {
    return humanizeSlug(category.slug);
  }
}
